import { useEffect } from 'react';
import { runAfterAuthHandoff } from '../../ui/splash/authHandoffHold';
import { supabase } from '../../supabase';
import { kickSessionRevoked } from '../../utils/auth/kickSessionRevoked';
import { getCurrentSessionIdFromAccessToken } from '../../utils/auth/sessionDeviceLabel';
import {
  filterSignalsForSessionWatermark,
  sessionStartWatermarkMs,
  shouldSignOutForRevocationSignal,
} from '../../utils/auth/sessionRevocationSignal';

function accessTokenIatSec(accessToken) {
  try {
    const payload = JSON.parse(
      atob(accessToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    );
    return Number.isFinite(payload?.iat) ? payload.iat : null;
  } catch (_) {
    return null;
  }
}

/**
 * Realtime + catch-up for public.user_session_revocation_signal.
 * Mirrors useMembershipRoleWatch: force context signOut when this device's
 * session was revoked elsewhere. Watermarked so re-login is not killed by
 * historical revoke_all rows.
 */
export function useSessionRevocationWatch({
  sessionUserId,
  loading,
  signOut,
  setAuthNotice,
}) {
  useEffect(() => {
    if (!sessionUserId || loading) return undefined;

    let cancelled = false;
    let channel = null;

    const kick = () => kickSessionRevoked({ signOut, setAuthNotice });

    const applySignals = async (rows) => {
      const { data: { session } = {} } = await supabase.auth.getSession();
      if (cancelled || !session) return;

      const localId = getCurrentSessionIdFromAccessToken(session.access_token);
      if (!localId) return;

      const watermarkMs = sessionStartWatermarkMs({
        accessTokenIatSec: accessTokenIatSec(session.access_token),
      });
      if (watermarkMs == null) return;

      const relevant = filterSignalsForSessionWatermark(rows, watermarkMs);
      for (const row of relevant) {
        if (!shouldSignOutForRevocationSignal(row, localId)) continue;
        try {
          await supabase.removeAllChannels();
        } catch (_) {
          /* best effort */
        }
        await kick();
        return;
      }
    };

    const run = async () => {
      try {
        const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const { data } = await supabase
          .from('user_session_revocation_signal')
          .select('id, user_id, created_at, revoked_session_id, except_session_id, revoke_all')
          .eq('user_id', sessionUserId)
          .gte('created_at', since)
          .order('created_at', { ascending: false })
          .limit(50);
        if (!cancelled && Array.isArray(data) && data.length) {
          await applySignals(data);
        }
      } catch (_) {
        /* non-fatal */
      }

      if (cancelled) return;

      channel = supabase
        .channel(`session-revoke-watch-${sessionUserId}`, { config: { private: true } })
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'user_session_revocation_signal',
            filter: `user_id=eq.${sessionUserId}`,
          },
          (payload) => {
            void applySignals([payload.new]);
          },
        )
        .subscribe();
    };

    // Waits out a sign-in handoff (authHandoffHold.js); run() replays the last
    // 24h of signals first, so nothing is missed by starting a moment later.
    const cancelDeferred = runAfterAuthHandoff(() => { void run(); }, { first: true });

    return () => {
      cancelDeferred();
      cancelled = true;
      if (channel) void supabase.removeChannel(channel);
    };
  }, [sessionUserId, loading, signOut, setAuthNotice]);
}
